import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { fm } from 'Components/FormatMessage';
import colors from '../../../../../../config/theme/colors';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TESSearchFields } from '../../definitions';
import Unit from 'Components/Unit';
import { resMedDeviceTypeToModesMap } from '../../config/sleepApnea';
import { StyledTitle } from '..';
import { getMinMaxFieldNames, getMinMaxFieldParent } from '../../functions/utils';

const PapDeviceField = ({ searchFields, onChange, value }: IOwnProps): React.JSX.Element => {
  const thisSearchField =
    searchFields.papDevice && searchFields.papDevice !== 'noCropping' ? searchFields.papDevice : undefined;

  const handleChange = (values: TOnChangeValues) => {
    let f = Object.keys(values)[0];
    let v = values[f];

    if (f === 'durationWeeks' && typeof v !== 'number') v = 'noCropping';
    if (f === 'papDeviceMode' && (!Array.isArray(v) || v.length === 0)) v = 'noCropping';
    if (f.includes('min') || f.includes('max')) {
      const parentField = getMinMaxFieldParent(f);
      const fieldNames = getMinMaxFieldNames(parentField);
      const parentValue = (thisSearchField?.[parentField as keyof typeof thisSearchField] ?? {
        [fieldNames[0]]: undefined,
        [fieldNames[1]]: undefined,
      }) as { [key: string]: number | undefined };
      v = { ...parentValue, [f]: v };
      if (Object.values(v).every(val => typeof val !== 'number')) v = 'noCropping';
      f = parentField;
    }

    const baseValue = typeof thisSearchField === 'object' ? thisSearchField : {};
    const newValues: Partial<typeof thisSearchField> = { ...baseValue, [f]: v };

    // Always (re)set papDeviceMode when changing papDeviceType
    if (f === 'papDeviceType') newValues.papDeviceMode = 'noCropping';

    onChange({ papDevice: newValues });
  };

  return (
    <React.Fragment>
      <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, marginBottom: '1rem' }}>
        {fm('extendedSearch.papDeviceType')}
      </div>
      <InputHandler
        type="Select"
        editing={true}
        name="papDeviceType"
        formData={{
          onChange: handleChange,
          document: { papDeviceType: value === 'noCropping' ? undefined : thisSearchField?.papDeviceType },
        }}
        options={Array.from(resMedDeviceTypeToModesMap.keys())}
        placeholder="extendedSearch.papDeviceTypePlaceholder"
      />
      {thisSearchField?.papDeviceType && (
        <React.Fragment>
          <StyledTitle>{fm('extendedSearch.papDeviceMode')}</StyledTitle>
          <InputHandler
            type="Checkbox"
            editing={true}
            name="papDeviceMode"
            formData={{
              onChange: handleChange,
              document: { papDeviceMode: thisSearchField.papDeviceMode },
            }}
            options={resMedDeviceTypeToModesMap.get(thisSearchField.papDeviceType)}
            optionFormatter={(name: string | number): string | React.JSX.Element =>
              name === 'noCropping' ? fm(`extendedSearch.opts.${name}`) : `${name}`
            }
          />
        </React.Fragment>
      )}
      <StyledTitle>{fm('extendedSearch.durationWeeks')}</StyledTitle>
      <Unit unit={fm('extendedSearch.weeksUnit')}>
        <React.Fragment>
          {['minDurationWeeks', 'maxDurationWeeks'].map((f, i) => {
            return (
              <React.Fragment key={`${f}-${i}`}>
                <InputHandler
                  type="NumberField"
                  editing={true}
                  name={f}
                  formData={{
                    onChange: handleChange,
                    document: {
                      [f]:
                        thisSearchField?.durationWeeks === 'noCropping'
                          ? undefined
                          : thisSearchField?.durationWeeks?.[f as keyof typeof thisSearchField.durationWeeks],
                    },
                  }}
                  min={1}
                  max={52}
                  placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[i]}`}
                  width={7}
                />
                {i === 0 && ' - '}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      </Unit>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {
  value: TESSearchFields[keyof TESSearchFields];
}

export default PapDeviceField;
